import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { apiRequest } from "../../Utils/apiClient";
import { resetStore } from "./resetStore";

export const fetchNotes = createAsyncThunk(
    "notes/fetchNotes",
    async (uid, { rejectWithValue }) => {
        try {
            const res = await apiRequest("GET", `/notes/${uid}`);
            return res?.data || res;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e?.message || "Fetch notes failed");
        }
    }
);

export const addNote = createAsyncThunk(
    "notes/addNote",
    async ({ uid, note }, { rejectWithValue }) => {
        try {
            const res = await apiRequest("POST", "/notes", { ...note, uid });
            return res?.data?.note || res?.data || res;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e?.message || "Add note failed");
        }
    }
);

export const updateNote = createAsyncThunk(
    "notes/updateNote",
    async ({ id, note }, { rejectWithValue }) => {
        try {
            const res = await apiRequest("PUT", `/notes/note/${id}`, note);
            return res?.data?.note || res?.data || res;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e?.message || "Update note failed");
        }
    }
);

export const toggleNoteCompleted = createAsyncThunk(
    "notes/toggleNoteCompleted",
    async ({ id }, { rejectWithValue }) => {
        try {
            const res = await apiRequest("PATCH", `/notes/note/${id}/toggle`);
            return res?.data?.note || res?.data || res;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e?.message || "Toggle note failed");
        }
    }
);

export const deleteNote = createAsyncThunk(
    "notes/deleteNote",
    async ({ id }, { rejectWithValue }) => {
        try {
            await apiRequest("DELETE", `/notes/note/${id}`);
            return id;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e?.message || "Delete note failed");
        }
    }
);

export const addNoteAndRefetch = createAsyncThunk(
    "notes/addNoteAndRefetch",
    async ({ uid, note }, { dispatch }) => {
        await dispatch(addNote({ uid, note })).unwrap();
        await dispatch(fetchNotes(uid));
        return true;
    }
);

const initialState = {
    byUid: {},
    status: "idle",
    error: null,
};

const replaceInByUid = (state, note) => {
    if (!note || note.id == null) return;

    const uid = note.uid || note._fetchedForUid;

    if (uid != null) {
        const arr = state.byUid[uid] || [];
        const index = arr.findIndex((n) => n?.id === note.id);

        if (index !== -1) arr[index] = note;
        else arr.unshift(note);

        state.byUid[uid] = arr;
        return;
    }

    Object.keys(state.byUid || {}).forEach((u) => {
        const arr = state.byUid[u] || [];
        const index = arr.findIndex((n) => n?.id === note.id);

        if (index !== -1) arr[index] = note;
        state.byUid[u] = arr;
    });
};

const notesSlice = createSlice({
    name: "notes",
    initialState,
    reducers: {
        toggleNoteLocal: (state, action) => {
            const { id } = action.payload;

            Object.keys(state.byUid || {}).forEach((uid) => {
                const arr = state.byUid[uid] || [];
                const index = arr.findIndex((n) => n?.id === id);

                if (index !== -1) {
                    const next = !arr[index].isCompleted;

                    arr[index] = {
                        ...arr[index],
                        isCompleted: next,
                        completedAt: next ? new Date().toISOString() : null,
                    };
                }
            });
        },

        patchNoteLocal: (state, action) => {
            const { id, changes } = action.payload;

            Object.keys(state.byUid || {}).forEach((uid) => {
                const arr = state.byUid[uid] || [];
                const index = arr.findIndex((n) => n?.id === id);

                if (index !== -1) {
                    arr[index] = { ...arr[index], ...changes };
                }
            });
        },

        removeNoteLocal: (state, action) => {
            const id = action.payload;

            Object.keys(state.byUid || {}).forEach((uid) => {
                state.byUid[uid] = (state.byUid[uid] || []).filter((n) => n.id !== id);
            });
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchNotes.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchNotes.fulfilled, (state, action) => {
                const uid = action.meta.arg;

                state.status = "succeeded";
                state.byUid[uid] = Array.isArray(action.payload)
                    ? action.payload.map((n) => ({ ...n, _fetchedForUid: uid }))
                    : [];
            })
            .addCase(fetchNotes.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || action.error?.message;
            })

            .addCase(addNote.fulfilled, (state, action) => {
                replaceInByUid(state, action.payload);
            })

            .addCase(updateNote.fulfilled, (state, action) => {
                replaceInByUid(state, action.payload);
            })

            .addCase(toggleNoteCompleted.fulfilled, (state, action) => {
                replaceInByUid(state, action.payload);
            })

            .addCase(deleteNote.fulfilled, (state, action) => {
                const id = action.payload;

                Object.keys(state.byUid || {}).forEach((uid) => {
                    state.byUid[uid] = (state.byUid[uid] || []).filter((n) => n.id !== id);
                });
            })

            .addCase(resetStore, () => initialState);
    },
});

export const {
    toggleNoteLocal,
    patchNoteLocal,
    removeNoteLocal,
} = notesSlice.actions;

export const selectNotesForUid = (state, uid) =>
    state.notes?.byUid?.[uid] || [];

export const selectNotesByDate = createSelector(
    [(state) => state.notes.byUid, (_, date) => date],
    (byUid, date) => {
        if (!byUid || !date) return [];

        return Object.keys(byUid).flatMap((uid) =>
            (byUid[uid] || []).filter((n) => n.date === date)
        );
    }
);

export default notesSlice.reducer;